import { Injectable, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService, type CreateHotToastRef } from '@ngxpert/hot-toast';
import { WINDOW } from '@tvd/cdk/browser';
import { OfflineToast } from '@tvd/ui/offline-toast';
import { fromEvent, map, merge, startWith } from 'rxjs';

const OFFLINE_TOAST_ID = 'connectivity-offline';

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  readonly #window = inject(WINDOW);
  readonly #toast = inject(HotToastService);
  readonly #translate = inject(TranslateService);

  /** Monotonically increasing signal — incremented each time the app reconnects after being offline. */
  readonly reconnected = signal(0);

  readonly isOnline = toSignal(
    merge(
      fromEvent(this.#window, 'online').pipe(map(() => true)),
      fromEvent(this.#window, 'offline').pipe(map(() => false)),
    ).pipe(startWith(this.#window.navigator.onLine)),
    { initialValue: this.#window.navigator.onLine },
  );

  #wasOffline = false;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #offlineToastRef: CreateHotToastRef<unknown> | null = null;

  readonly #onlineChange = effect(() => {
    const online = this.isOnline();
    if (!online) {
      this.#wasOffline = true;
      this.#showOfflineToast();
      if (this.#reconnectTimer) {
        clearTimeout(this.#reconnectTimer);
        this.#reconnectTimer = null;
      }
    } else {
      this.#dismissOfflineToast();
      if (this.#wasOffline) {
        this.#wasOffline = false;
        this.#toast.success(this.#translate.instant('connectivity.online'));
        this.#reconnectTimer = setTimeout(() => {
          this.reconnected.update((v) => v + 1);
          this.#reconnectTimer = null;
        }, 2_000);
      }
    }
  });

  #showOfflineToast(): void {
    if (this.#offlineToastRef) return;
    this.#offlineToastRef = this.#toast.show(OfflineToast, {
      id: OFFLINE_TOAST_ID,
      dismissible: true,
      autoClose: false,
      data: { message: this.#translate.instant('connectivity.offline') },
    });
  }

  #dismissOfflineToast(): void {
    if (this.#offlineToastRef) {
      this.#offlineToastRef.close();
      this.#offlineToastRef = null;
    }
  }
}
